import { Tag } from '@components';
import { Box, Tooltip, useTheme } from '@mui/material';
import { type FC, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { SlabRoute } from '../../SlabRoutes';
import { ListItem, ListItemButton, ListItemIcon, ListItemText } from './styles';

interface AsideOptionProps {
  route: SlabRoute;
  isOpen: boolean;
  isMobile: boolean;
  closeDrawer(): void;
}

const NavOption: FC<AsideOptionProps> = ({ route, isOpen, closeDrawer, isMobile }) => {
  const theme = useTheme();
  const location = useLocation();

  const navigate = useNavigate();

  const isActive = useMemo(() => {
    const currentPath = location.pathname.split('/')[1] ?? '';

    return route.path === currentPath;
  }, [route.path, location.pathname]);

  const handleOptionClick = (): void => {
    if (isMobile) closeDrawer();

    navigate(route.path);
  };

  if (route.icon === undefined || route?.icon?.() === null) return null;

  return (
    <ListItem disablePadding>
      <Tooltip
        title={isOpen ? '' : route.name}
        placement='right'
        enterDelay={200}
        arrow
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.SlabGray[950],
              color: isActive ? theme.palette.SlabYellow[500] : theme.palette.SlabGray[400],
              padding: '6px 12px',
              fontSize: '1rem',
              borderRadius: 4,
              fontWeight: 500,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
            },
          },
          arrow: {
            sx: {
              color: theme.palette.SlabGray[950],
              fontSize: '8px',
            },
          },
        }}
        disableHoverListener={isOpen}
        disableFocusListener={isOpen}
        disableTouchListener={isOpen}
      >
        <ListItemButton onClick={handleOptionClick} isOpen={isOpen}>
          <ListItemIcon>
            {route.icon(isActive ? theme.palette.SlabYellow[500] : theme.palette.SlabGray['500'])}
          </ListItemIcon>
          {route.newFeature && !isOpen ? (
            <Box
              sx={{
                position: 'absolute',
                top: '4px',
                right: '16px',
                backgroundColor: theme.palette.SlabIndigo[700],
                width: '8px',
                height: '8px',
                borderRadius: '8px',
              }}
            />
          ) : null}
          <ListItemText isActive={isActive} primary={route.name} isOpen={isOpen} />

          {route.newFeature && isOpen ? <Tag label='New' variant='New' size='sm' /> : null}

          {isActive && (
            <Box
              sx={{
                borderTopLeftRadius: 10,
                borderBottomLeftRadius: 10,
              }}
              right={0}
              bottom={0}
              top={0}
              position='absolute'
              height='100%'
              bgcolor={theme.palette.SlabYellow[500]}
              width='3px'
            />
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default NavOption;
